import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { authAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

const ROLE_IS = {
  owner: 'Eigandi',
  admin: 'Stjórnandi',
  manager: 'Verkstjóri',
  bookkeeper: 'Bókari',
  worker: 'Starfsmaður',
};

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await authAPI.getInviteInfo(token);
        setInvite(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Ógilt eða útrunnið boð');
      } finally {
        setLoading(false);
      }
    };
    if (token) load();
    else { setError('Enginn hlekkur'); setLoading(false); }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) { setError('Lykilorð stemma ekki'); return; }
    if (password.length < 6) { setError('Lykilorð verður að vera að minnsta kosti 6 stafir'); return; }
    setSubmitting(true);
    try {
      const res = await authAPI.acceptInvite({ token, name, password });
      const { token: jwtToken, user: userData } = res.data;
      localStorage.setItem('auth_token', jwtToken);
      localStorage.setItem('user', JSON.stringify(userData));
      // Update AuthContext state to authenticate the user
      await checkAuth();
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Villa við skráningu');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin text-accent" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img
            src="/logos/logo-white.png"
            alt="REKTRA"
            className="h-10 w-auto object-contain mx-auto"
            data-testid="invite-logo"
          />
        </div>

        {error && !invite ? (
          <div className="card p-6 text-center space-y-3" data-testid="invite-error">
            <p className="text-error">{error}</p>
            <Link to="/login" className="btn btn-primary inline-flex items-center gap-2 mt-4">
              Til baka
            </Link>
          </div>
        ) : invite && (
          <div>
            <h2 className="text-xl font-semibold mb-2">Samþykkja boð</h2>
            <p className="text-foreground-secondary text-sm mb-1" data-testid="invite-intro">
              {invite.inviter_name} hefur boðið þér aðgang að{' '}
              <strong>{invite.company_name || 'Rektra'}</strong>
              {/* The name is shown exactly as stored; a name that already
                  ends in a period does not get a second one. */}
              {(invite.company_name || 'Rektra').endsWith('.') ? '' : '.'}
            </p>
            <p className="text-foreground-muted text-xs mb-6">
              {invite.email} &middot; {ROLE_IS[invite.role] || invite.role}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="accept-invite-form">
              {error && (
                <div className="p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">{error}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground-secondary mb-2">Fullt nafn</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nafn þitt" required data-testid="invite-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-secondary mb-2">Lykilorð</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Að minnsta kosti 6 stafir"
                    required
                    data-testid="invite-password"
                    className="pr-11"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground">
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-secondary mb-2">Staðfesta lykilorð</label>
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Endurtaka lykilorð" required data-testid="invite-confirm" />
              </div>
              <button type="submit" disabled={submitting} className="w-full btn btn-primary btn-lg" data-testid="accept-invite-submit">
                {submitting ? <Loader2 size={18} className="animate-spin" /> : 'Samþykkja boð'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
